import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {favoriteListsApi} from 'api/favoriteListsApi';
import {getRequestStatus} from 'helper/requestStatus';

const EMPTY_ARRAY = [];

const fetchFavoriteLists = createAsyncThunk(
  'fetchFavoriteLists',
  async ({soldTo}) => {
    const lists = await favoriteListsApi.getFavoriteLists(soldTo);
    const details = await Promise.all(
      lists.map(list => favoriteListsApi.getFavoriteList(soldTo, list.id))
    );

    const membership = {};
    const listDetails = {};
    details.forEach(detail => {
      listDetails[detail.id] = detail.wines;
      detail.wines.forEach(wine => {
        if (!membership[wine.wineId]) {
          membership[wine.wineId] = [];
        }
        membership[wine.wineId].push(detail.id);
      });
    });

    return {
      lists: lists.map(({id, name, description, isDefault, wineCount}) => ({id, name, description, isDefault, wineCount})),
      membership,
      listDetails,
      favoritedWineIds: Object.keys(membership),
    };
  }
);

const createFavoriteList = createAsyncThunk(
  'createFavoriteList',
  async ({soldTo, name, description}, {dispatch, rejectWithValue}) => {
    try {
      const created = await favoriteListsApi.createFavoriteList(soldTo, name, description);
      await dispatch(fetchFavoriteLists({soldTo}));
      return created;
    } catch (error) {
      return rejectWithValue({status: error.response?.status});
    }
  }
);

const renameFavoriteList = createAsyncThunk(
  'renameFavoriteList',
  async ({soldTo, favoriteListId, name, description}, {dispatch, rejectWithValue}) => {
    try {
      const updated = await favoriteListsApi.updateFavoriteList(soldTo, favoriteListId, name, description);
      await dispatch(fetchFavoriteLists({soldTo}));
      return updated;
    } catch (error) {
      return rejectWithValue({status: error.response?.status});
    }
  }
);

const deleteFavoriteList = createAsyncThunk(
  'deleteFavoriteList',
  async ({soldTo, favoriteListId}, {dispatch}) => {
    await favoriteListsApi.deleteFavoriteList(soldTo, favoriteListId);
    await dispatch(fetchFavoriteLists({soldTo}));
    return {favoriteListId};
  }
);

const addItemToList = createAsyncThunk(
  'addItemToList',
  async ({soldTo, favoriteListId, wineId}, {dispatch, rejectWithValue}) => {
    try {
      await favoriteListsApi.addItem(soldTo, favoriteListId, wineId);
      await dispatch(fetchFavoriteLists({soldTo}));
    } catch (error) {
      return rejectWithValue({status: error.response?.status});
    }
  }
);

const removeItemFromList = createAsyncThunk(
  'removeItemFromList',
  async ({soldTo, favoriteListId, wineId}, {dispatch, rejectWithValue}) => {
    try {
      await favoriteListsApi.removeItem(soldTo, favoriteListId, wineId);
      await dispatch(fetchFavoriteLists({soldTo}));
    } catch (error) {
      return rejectWithValue({status: error.response?.status});
    }
  }
);

const setWineMembership = createAsyncThunk(
  'setWineMembership',
  async ({soldTo, wineId, favoriteListIds}, {dispatch}) => {
    const result = await favoriteListsApi.setMembership(soldTo, wineId, favoriteListIds);
    await dispatch(fetchFavoriteLists({soldTo}));
    return result;
  }
);

const removeWineFromAllLists = createAsyncThunk(
  'removeWineFromAllLists',
  async ({soldTo, wineId}, {dispatch}) => {
    const result = await favoriteListsApi.removeFromAllLists(soldTo, wineId);
    await dispatch(fetchFavoriteLists({soldTo}));
    return result;
  }
);

const favoriteListsSlice = createSlice({
  name: 'favoriteLists',
  initialState: {
    lists: null,
    membership: {},
    listDetails: {},
    favoritedWineIds: EMPTY_ARRAY,
    activeListId: null,
    requests: {
      fetchFavoriteLists: {
        loading: false,
        error: null,
      },
    },
  },
  reducers: {
    resetFavoriteLists: (state) => {
      state.lists = null;
      state.membership = {};
      state.listDetails = {};
      state.favoritedWineIds = EMPTY_ARRAY;
      state.activeListId = null;
      state.requests.fetchFavoriteLists.loading = false;
      state.requests.fetchFavoriteLists.error = null;
    },
    setActiveFavoriteList: (state, action) => {
      state.activeListId = action.payload;
    },
  },
  extraReducers: {
    [fetchFavoriteLists.pending]: (state) => {
      state.requests.fetchFavoriteLists.loading = true;
      state.requests.fetchFavoriteLists.error = null;
    },
    [fetchFavoriteLists.fulfilled]: (state, action) => {
      state.requests.fetchFavoriteLists.loading = false;
      state.requests.fetchFavoriteLists.error = null;
      state.lists = action.payload.lists;
      state.membership = action.payload.membership;
      state.listDetails = action.payload.listDetails;
      state.favoritedWineIds = action.payload.favoritedWineIds;
      const activeListStillExists = state.lists.some(list => list.id === state.activeListId);
      if (!activeListStillExists) {
        const defaultList = state.lists.find(list => list.isDefault);
        state.activeListId = defaultList ? defaultList.id : (state.lists[0] ? state.lists[0].id : null);
      }
    },
    [fetchFavoriteLists.rejected]: (state, action) => {
      state.requests.fetchFavoriteLists.loading = false;
      state.requests.fetchFavoriteLists.error = action.error.message;
    },
  },
});

export const favoriteListsSelectors = {
  favoriteLists: state => state.favoriteLists.lists,
  favoriteListsRequest: state => getRequestStatus(
    state.favoriteLists.requests.fetchFavoriteLists,
    state.favoriteLists.lists !== null,
  ),
  isWineFavorited: wineId => state => (state.favoriteLists.membership[wineId] ?? EMPTY_ARRAY).length > 0,
  wineListMembership: wineId => state => state.favoriteLists.membership[wineId] ?? EMPTY_ARRAY,
  allFavoritedWineIds: state => state.favoriteLists.favoritedWineIds,
  listDetails: state => state.favoriteLists.listDetails,
  activeListId: state => state.favoriteLists.activeListId,
  activeListWines: state => state.favoriteLists.activeListId !== null
    ? (state.favoriteLists.listDetails[state.favoriteLists.activeListId] ?? EMPTY_ARRAY)
    : EMPTY_ARRAY,
};

const actions = {
  ...favoriteListsSlice.actions,
  fetchFavoriteLists,
  createFavoriteList,
  renameFavoriteList,
  deleteFavoriteList,
  addItemToList,
  removeItemFromList,
  setWineMembership,
  removeWineFromAllLists,
};
export {actions as favoriteListsActions};

export default favoriteListsSlice.reducer;
